import { createSearchParams, useNavigate } from 'react-router-dom';
import { useMemo, useState } from 'react';
import moment from 'moment';
import {
  PAID_STATUSES,
  useAppointmentHasuraStatus,
} from './getStatusClassName';
import {
  AppointmentHasuraStatus,
  MIN_RESCHEDULE_TIME,
} from '../../../const/appointment';
import { useIsJoinable } from 'hooks/useIsJoinable';
import { useGetAppointmentPaymentLinkLazyQuery } from 'graphql/generated/remote-schema-hasura';
import { useGetComponent } from 'hooks/useGetComponent';
import { componentIds } from 'utilities/constants';
import { AppointmentTypeEnum } from 'graphql/generated/hasura';
import {
  MY_APPOINTMENTS_CHAT_CONSULTATION,
  MY_APPOINTMENTS_RESCHEDULE_AN_APPOINTMENT,
  MY_APPOINTMENTS_VIDEO_CONSULTATION,
} from 'utilities/routes';
import { UpcomingAppoinment } from '../../../interfaces';
import { useAuth } from 'auth/context/AuthContext';
import { Roles } from '../../../../../firebase/interfaces';
import { CANCEL_APPOINTMENT_ID } from './const';
import { UpcomingAppointments } from '../../../../../hooks/useGetUpcomingAppointment';

const ADD_NOT_TO_CALENDAR_STATUSES = [
  AppointmentHasuraStatus.Canceled,
  AppointmentHasuraStatus.Completed,
  AppointmentHasuraStatus.PatientNotShowingUp,
  AppointmentHasuraStatus.ProviderNotShowingUp,
  AppointmentHasuraStatus.NobodyNotShowingUp,
];

const CHAT_TYPES = [
  AppointmentTypeEnum.Chat,
  AppointmentTypeEnum.Chat_30,
  AppointmentTypeEnum.Chat_60,
];

export const getJoinAppointment = ({
  appointmentType,
  appointmentCodexId,
  patientCodexId,
  providerCodexId,
}: Pick<
  UpcomingAppointments,
  | 'appointmentCodexId'
  | 'appointmentType'
  | 'patientCodexId'
  | 'providerCodexId'
>) => {
  const type = appointmentType.split('_')[0];
  const base = CHAT_TYPES.includes(type as AppointmentTypeEnum)
    ? MY_APPOINTMENTS_CHAT_CONSULTATION
    : MY_APPOINTMENTS_VIDEO_CONSULTATION;
  return {
    pathname: `${base}&appointment-id=${appointmentCodexId}`,
    state: { providerId: providerCodexId, patientId: patientCodexId },
  };
};

export const useAppointmentCardHelper = ({
  date,
  appointmentStatus,
  end,
  appointmentId,
  appointmentType,
  paymentInformation,
  patientId,
  providerId,
}: UpcomingAppoinment) => {
  const navigate = useNavigate();
  const [opened, setOpened] = useState<boolean>(false);
  const { user } = useAuth();

  const discountValue = useMemo(() => {
    const { amountOff, percentOff } = paymentInformation?.discount ?? {};
    return (
      (amountOff ?? 0) / 100 ||
      ((Number(paymentInformation?.amount) || 0) * (percentOff || 0)) / 100
    );
  }, [paymentInformation]);

  const isProvider = useMemo(() => user?.role === Roles.PROVIDER, [user]);

  const {
    label,
    Icon,
    iconClassNames,
    notification,
    statusClassName,
    notificationClassNames,
  } = useAppointmentHasuraStatus(appointmentStatus as AppointmentHasuraStatus);

  const hasPastTimeRescheduleAppointment = useMemo(() => {
    const duration = moment.duration(moment(date).diff(moment()));
    return duration.asHours() < MIN_RESCHEDULE_TIME;
  }, [date]);

  const hasPastTimeCancelAppointment = useMemo(() => {
    return (
      moment(date).isBefore(moment()) ||
      appointmentStatus === AppointmentHasuraStatus.Canceled
    );
  }, [date, appointmentStatus]);

  const { isJoinable } = useIsJoinable({
    startDate: date,
    endDate: end,
    appointmentStatus,
  });
  const isNotPaid =
    !isProvider &&
    PAID_STATUSES.includes(appointmentStatus as AppointmentHasuraStatus);

  const showReceipt =
    !isProvider &&
    !PAID_STATUSES.includes(appointmentStatus as AppointmentHasuraStatus) &&
    (paymentInformation?.amount || 0) - discountValue > 0;

  const isAddToCalendar =
    appointmentStatus &&
    !ADD_NOT_TO_CALENDAR_STATUSES.includes(appointmentStatus);

  const [getStripeLink, { loading }] = useGetAppointmentPaymentLinkLazyQuery({
    variables: { appointmentCodexId: String(appointmentId) },
  });

  const { data: locale } = useGetComponent({
    componentId: componentIds.UPCOMING_APPOINTMENTS_PROVIDER,
    locale: 'en',
  });

  const handleRetryPayment = async () => {
    try {
      const { data } = await getStripeLink();
      window.location.href = data?.getAppointmentRetryPaymentLink?.link || '';
    } catch (e) {
      console.error(e);
    }
  };

  const handleJoinAppointment = () => {
    const { pathname, state } = getJoinAppointment({
      appointmentType,
      appointmentCodexId: appointmentId,
      patientCodexId: patientId,
      providerCodexId: providerId,
    });
    navigate(pathname, { state });
  };

  const handleClickReschedule = () =>
    navigate(`${MY_APPOINTMENTS_RESCHEDULE_AN_APPOINTMENT}/${appointmentId}`);

  const handleClickCancel = () =>
    navigate({
      search: createSearchParams({
        [CANCEL_APPOINTMENT_ID]: String(appointmentId),
      }).toString(),
    });

  return {
    handleJoinAppointment,
    handleRetryPayment,
    handleClickCancel,
    handleClickReschedule,
    locale,
    label,
    loading,
    opened,
    setOpened,
    isJoinable,
    isNotPaid,
    isProvider,
    showReceipt,
    Icon,
    iconClassNames,
    notification,
    notificationClassNames,
    statusClassName,
    hasPastTimeCancelAppointment,
    hasPastTimeRescheduleAppointment,
    isAddToCalendar,
  };
};
